/**
 * Dashboard widget: revenue (CA), purchase cost in the same calendar window, margin, order counts.
 * Admin and manager – sensitive financial data per NFR-S8.
 */
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { getRevenueSummary } from "../../services/revenueService";
import { formatCurrency } from "../../utils/currency";
import type { RevenueSummary as RevenueSummaryType } from "../../types/models";
import { getApiErrorMessage } from "../../utils/apiError";

interface PeriodRowGetter {
  labelKey: string;
  get: (s: RevenueSummaryType) => {
    orders: number;
    revenue: number;
    cost: number;
    margin: number;
  };
}

const REVENUE_PERIOD_ROWS: PeriodRowGetter[] = [
  {
    labelKey: "summary_today",
    get: (s) => ({
      orders: s.today_order_count ?? 0,
      revenue: s.today ?? 0,
      cost: s.today_purchase_cost ?? 0,
      margin: s.today_margin ?? 0,
    }),
  },
  {
    labelKey: "summary_last_7_days",
    get: (s) => ({
      orders: s.last_7_days_order_count ?? 0,
      revenue: s.last_7_days ?? 0,
      cost: s.last_7_days_purchase_cost ?? 0,
      margin: s.last_7_days_margin ?? 0,
    }),
  },
  {
    labelKey: "summary_current_week",
    get: (s) => ({
      orders: s.current_week_order_count ?? 0,
      revenue: s.current_week_revenue ?? 0,
      cost: s.current_week_purchase_cost ?? 0,
      margin: s.current_week_margin ?? 0,
    }),
  },
  {
    labelKey: "summary_last_week",
    get: (s) => ({
      orders: s.last_week_order_count ?? 0,
      revenue: s.last_week_revenue ?? 0,
      cost: s.last_week_purchase_cost ?? 0,
      margin: s.last_week_margin ?? 0,
    }),
  },
  {
    labelKey: "summary_current_month",
    get: (s) => ({
      orders: s.current_month_order_count ?? 0,
      revenue: s.current_month ?? 0,
      cost: s.current_month_purchase_cost ?? 0,
      margin: s.current_month_margin ?? 0,
    }),
  },
  {
    labelKey: "summary_last_month",
    get: (s) => ({
      orders: s.last_month_order_count ?? 0,
      revenue: s.last_month ?? 0,
      cost: s.last_month_purchase_cost ?? 0,
      margin: s.last_month_margin ?? 0,
    }),
  },
  {
    labelKey: "summary_year_to_date",
    get: (s) => ({
      orders: s.current_year_order_count ?? 0,
      revenue: s.current_year_revenue ?? 0,
      cost: s.current_year_purchase_cost ?? 0,
      margin: s.current_year_margin ?? 0,
    }),
  },
];

function marginClassName(margin: number): string {
  if (margin > 0) return "text-emerald-700 dark:text-emerald-400";
  if (margin < 0) return "text-red-600 dark:text-red-400";
  return "text-neutral-900 dark:text-neutral-100";
}

export function RevenueSummary() {
  const { t } = useTranslation("dashboard");
  const [summary, setSummary] = useState<RevenueSummaryType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const rows = useMemo(() => {
    if (!summary) return [];
    return REVENUE_PERIOD_ROWS.map(({ labelKey, get }) => ({
      label: t(labelKey),
      ...get(summary),
    }));
  }, [summary, t]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getRevenueSummary()
      .then((data) => {
        if (!cancelled) setSummary(data);
      })
      .catch((err) => {
        if (!cancelled) {
          const msg = getApiErrorMessage(err, t("summary_error_revenue"));
          setError(msg);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-8 text-center">
        <div className="w-8 h-8 border-2 border-brand-teal border-t-transparent rounded-full animate-spin mb-3" />
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_loading_revenue")}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!summary) {
    return (
      <div className="py-4 text-center">
        <p className="text-sm text-neutral-500 dark:text-neutral-400">{t("summary_no_data")}</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <p className="text-[11px] leading-snug text-neutral-500 dark:text-neutral-400">
        {t("summary_revenue_dashboard_note")}
      </p>

      <div className="overflow-x-auto -mx-1 px-1">
        <table className="w-full text-sm tabular-nums border-collapse">
          <thead>
            <tr className="text-[11px] text-neutral-500 dark:text-neutral-400 font-normal uppercase tracking-wide border-b border-neutral-200 dark:border-neutral-700">
              <th className="py-1.5 pr-2 text-left font-medium normal-case">{t("summary_col_period")}</th>
              <th className="py-1.5 px-1 text-right font-medium">{t("summary_col_orders")}</th>
              <th className="py-1.5 px-1 text-right font-medium">{t("summary_col_revenue")}</th>
              <th className="py-1.5 px-1 text-right font-medium">{t("summary_col_purchases")}</th>
              <th className="py-1.5 pl-1 text-right font-medium">{t("summary_col_margin")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr
                key={row.label}
                className="border-b border-neutral-100 dark:border-neutral-700/80 last:border-b-0"
              >
                <td className="py-2 pr-2 text-left text-neutral-600 dark:text-neutral-300">{row.label}</td>
                <td className="py-2 px-1 text-right text-neutral-900 dark:text-neutral-100">{row.orders}</td>
                <td className="py-2 px-1 text-right font-medium text-neutral-900 dark:text-neutral-100">
                  {formatCurrency(row.revenue)}
                </td>
                <td className="py-2 px-1 text-right text-neutral-700 dark:text-neutral-200">
                  {formatCurrency(row.cost)}
                </td>
                <td className={`py-2 pl-1 text-right font-semibold ${marginClassName(row.margin)}`}>
                  {formatCurrency(row.margin)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex justify-between items-baseline gap-2 pt-1 text-xs border-t border-neutral-200 dark:border-neutral-700">
        <span className="text-neutral-500 dark:text-neutral-400">{t("summary_all_time_revenue")}</span>
        <span className="font-semibold text-neutral-900 dark:text-neutral-100 tabular-nums">
          {formatCurrency(summary.total_revenue ?? 0)}
        </span>
      </div>

      {summary.top_customers && summary.top_customers.length > 0 && (
        <div className="border-t border-neutral-200 dark:border-neutral-700 pt-2 mt-2">
          <p className="text-xs text-neutral-500 dark:text-neutral-400 mb-1.5">{t("summary_top_customers")}</p>
          <ul className="space-y-1.5">
            {summary.top_customers.map((c) => (
              <li key={c.customer_id} className="flex justify-between items-baseline gap-2 text-sm">
                <span className="text-neutral-700 dark:text-neutral-300 truncate">{c.name}</span>
                <span className="font-medium tabular-nums shrink-0">
                  {formatCurrency(c.total_revenue)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
